import {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useEffect,
  ReactNode,
} from "react";
import { supabase } from "@/integrations/supabase/client";
import { useInvalidateSOPs } from "@/hooks/useSOPs";
import { createSubscribeCallback } from "@/lib/realtimeErrorHandler";

/**
 * Extract sop_id from a Realtime payload with type safety
 */
function extractSopId(payload: unknown): string | null {
  if (!payload || typeof payload !== "object") return null;
  const p = payload as Record<string, unknown>;
  if (typeof p.sop_id === "string") return p.sop_id;
  return null;
}

interface RecalculatingContextType {
  recalculatingSOPs: Set<string>;
  addRecalculatingSOP: (sopId: string) => void;
  removeRecalculatingSOP: (sopId: string) => void;
  isRecalculating: (sopId: string) => boolean;
  isRecalculatingAutomationScore: (sopId: string) => boolean;
}

const RecalculatingContext = createContext<RecalculatingContextType | null>(
  null,
);

export function RecalculatingProvider({ children }: { children: ReactNode }) {
  // Track SOPs that are recalculating automation score
  const [recalculatingAutomationScore, setRecalculatingAutomationScore] =
    useState<Set<string>>(new Set());

  const { invalidateAll: invalidateSOPs } = useInvalidateSOPs();

  // Derived: an SOP is "recalculating" if automation score is being recalculated
  const recalculatingSOPs = recalculatingAutomationScore;

  const addRecalculatingSOP = useCallback((sopId: string) => {
    setRecalculatingAutomationScore((prev) => new Set(prev).add(sopId));
  }, []);

  const removeRecalculatingSOP = useCallback((sopId: string) => {
    setRecalculatingAutomationScore((prev) => {
      const next = new Set(prev);
      next.delete(sopId);
      return next;
    });
  }, []);

  const isRecalculating = useCallback(
    (sopId: string) => {
      return recalculatingAutomationScore.has(sopId);
    },
    [recalculatingAutomationScore],
  );

  const isRecalculatingAutomationScore = useCallback(
    (sopId: string) => {
      return recalculatingAutomationScore.has(sopId);
    },
    [recalculatingAutomationScore],
  );

  // Subscribe to realtime updates on sop_automation_scores
  // Remove from recalculating when it completes
  useEffect(() => {
    if (recalculatingAutomationScore.size === 0) return;

    const channel = supabase
      .channel("recalculating-scores")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "sop_automation_scores",
        },
        (payload) => {
          const sopId = extractSopId(payload.new) || extractSopId(payload.old);
          if (sopId && recalculatingAutomationScore.has(sopId)) {
            // Remove automation score from recalculating
            setRecalculatingAutomationScore((prev) => {
              const next = new Set(prev);
              next.delete(sopId);
              return next;
            });
            // Invalidate SOPs cache to fetch new score
            invalidateSOPs();
          }
        },
      )
      .subscribe(createSubscribeCallback("recalculating-scores"));

    return () => {
      supabase.removeChannel(channel);
    };
  }, [recalculatingAutomationScore, invalidateSOPs]);

  const contextValue = useMemo(
    () => ({
      recalculatingSOPs,
      addRecalculatingSOP,
      removeRecalculatingSOP,
      isRecalculating,
      isRecalculatingAutomationScore,
    }),
    [
      recalculatingSOPs,
      addRecalculatingSOP,
      removeRecalculatingSOP,
      isRecalculating,
      isRecalculatingAutomationScore,
    ],
  );

  return (
    <RecalculatingContext.Provider value={contextValue}>
      {children}
    </RecalculatingContext.Provider>
  );
}

export function useRecalculating() {
  const context = useContext(RecalculatingContext);
  if (!context) {
    throw new Error(
      "useRecalculating must be used within a RecalculatingProvider",
    );
  }
  return context;
}
